import { apiGet, refusalWords } from './client';
import type { OperationResult } from './types';
import type { ResultStateName } from './runEvidence';

// ─────────────────────────────────────────────────────────────────────────────
// Scenario Intelligence screen 01 — the Overview's worklist (vNEXT EvaluationWorklist.cs, GET
// api/ValidationRuns/Agents/{id}/Worklist). Every judgement is the server's: which result counts for the current
// version (EVAL-06), what state it is in (SI-03b's reading), why a scenario is Not assessed, coverage per category.
// The screen only words them, and never adds a state, a score or a coverage claim of its own.
// ─────────────────────────────────────────────────────────────────────────────

/** Why a library scenario has no result that counts on its current version (EvaluationWorklistRules.NotAssessedKinds). */
export type NotAssessedKind =
  | 'NeverRun'
  | 'EditedSinceRun'
  | 'EarlierAgentVersion'
  | 'VersionNotRecorded'
  | 'TooOld'
  | 'AgentVersionUnreadable'
  | 'Quarantined'
  | 'DidNotComplete'
  | 'NotRead';

/** The one next action a row offers (EvaluationWorklistRules.Actions). */
export type WorklistAction = 'OpenFinding' | 'Run' | 'None';

export interface WorklistItem {
  scenarioId: string;
  title?: string | null;
  category: string;
  isSafety: boolean;
  exploratory: boolean;
  state: ResultStateName;
  /** One line: what the check found, or why nothing counts. */
  why: string;
  notAssessedKind?: NotAssessedKind | null;
  /** The result the state rests on; for a scenario with none, its latest result on any version (never counted). */
  resultId?: number | null;
  resultCounts: boolean;
  runId?: number | null;
  runUtc?: string | null;
  /** Zero-based turn of the first recorded failure. */
  turn?: number | null;
  findingDisposition?: 'Open' | 'Acknowledged' | 'Resolved' | 'Disputed' | null;
  /** The cause a PERSON recorded under Review; never inferred. */
  findingCause?: string | null;
  nextAction: WorklistAction;
}

export interface WorklistCounts {
  library: number;
  needsAttention: number;
  evidenceIncomplete: number;
  notAssessed: number;
  passed: number;
  /** Contract v3: automatic checks passed, a reviewer's obligation waits. Absent from an API before 2026-11-02. */
  awaitingReview?: number;
}

export interface ExploratoryCounts {
  drafts: number;
  notRun: number;
  needsAttention: number;
  evidenceIncomplete: number;
  notAssessed: number;
  passed: number;
  awaitingReview?: number;
}

export interface NotAssessedGroup {
  kind: NotAssessedKind;
  count: number;
}

export interface CoverageRow {
  code: string;
  label: string;
  inTaxonomy: boolean;
  scenarios: number;
  needsAttention: number;
  evidenceIncomplete: number;
  notAssessed: number;
  passed: number;
  awaitingReview?: number;
}

export interface CoverageSummary {
  /** What is counted: "category" today. Requirement coverage arrives with SI-05b. */
  basis: string;
  domain?: string | null;
  rows: CoverageRow[];
  categories: number;
  covered: number;
  uncovered: number;
}

export interface WorklistRunningJob {
  jobId: number;
  state: string;
  completedTrials: number;
  expectedTrials?: number | null;
  submittedUtc: string;
  startedUtc?: string | null;
  runId?: number | null;
  scenarioId?: string | null;
  startedByViewer: boolean;
}

export interface EvaluationWorklist {
  agentId: number;
  agentName: string;
  merchantId: number;
  currentCandidateHash?: string | null;
  policyVersion: string;
  counts: WorklistCounts;
  exploratory: ExploratoryCounts;
  notAssessed: NotAssessedGroup[];
  items: WorklistItem[];
  coverage: CoverageSummary;
  latestRunId?: number | null;
  latestRunUtc?: string | null;
  evidenceRunIds: number[];
  untestedChanges: string[];
  retiredRequired: number;
  running?: WorklistRunningJob | null;
  notes: string[];
}

/** The agent's worklist in the selected practice. Throws with the server's sentence when it refuses. */
export async function getEvaluationWorklist(agentId: number, signal?: AbortSignal): Promise<EvaluationWorklist> {
  let wrapped: OperationResult<EvaluationWorklist>;
  try {
    wrapped = await apiGet<OperationResult<EvaluationWorklist>>(`/api/ValidationRuns/Agents/${agentId}/Worklist`, undefined, { signal });
  } catch (err) {
    throw new Error(refusalWords(err));
  }
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || `Could not load the worklist of agent #${agentId}`);
  return wrapped.data;
}

/** The agent last evaluated in the selected practice, or null when no run acted there yet. */
export async function getLastEvaluatedAgent(signal?: AbortSignal): Promise<number | null> {
  let wrapped: OperationResult<{ agentId?: number | null }>;
  try {
    wrapped = await apiGet<OperationResult<{ agentId?: number | null }>>('/api/ValidationRuns/Worklist/LastAgent', undefined, { signal });
  } catch (err) {
    throw new Error(refusalWords(err));
  }
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Could not tell which agent was evaluated last');
  return wrapped.data?.agentId ?? null;
}
